import { useState, type ChangeEvent, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/db6bgr22sgkaik1undeg";
const MESSAGE_LIMIT = 500;

interface FormValues {
  name: string;
  phone: string;
  email: string;
  message: string;
}

const emptyValues: FormValues = { name: "", phone: "", email: "", message: "" };

export default function ContactForm() {
  const [values, setValues] = useState<FormValues>(emptyValues);
  const [errors, setErrors] = useState<Partial<FormValues>>({});
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const validate = (vals: FormValues): Partial<FormValues> => {
    const errs: Partial<FormValues> = {};
    if (!vals.name.trim()) errs.name = "请填写您的姓名";
    if (!vals.phone.trim()) errs.phone = "请填写联系电话";
    else if (!/^[0-9+\-\s()]{6,20}$/.test(vals.phone.trim())) errs.phone = "请填写有效的联系电话";
    if (vals.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vals.email.trim()))
      errs.email = "邮箱格式不正确";
    if (!vals.message.trim()) errs.message = "请填写留言内容";
    else if (vals.message.length > MESSAGE_LIMIT) errs.message = "留言内容请控制在 500 字以内";
    return errs;
  };

  const handleChange =
    (field: keyof FormValues) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValues((v) => ({ ...v, [field]: e.target.value }));
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setValues(emptyValues);
      return;
    }

    const errs = validate(values);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setStatus("idle");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      const str = String(value).trim();
      if (str) payload.append(key, str);
    });

    setStatus("loading");
    setErrorMsg("");
    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK";

      if (isOk && !isSpam) {
        setStatus("success");
        form.reset();
        setValues(emptyValues);
      } else {
        setStatus("error");
        setErrorMsg(
          typeof serverMsg === "string" && serverMsg && !isSpam
            ? serverMsg
            : "提交失败，请检查内容后重试。"
        );
      }
    } catch {
      setStatus("error");
      setErrorMsg("网络异常，请稍后重试。");
    }
  };

  const inputClass = (hasError?: string) =>
    `w-full px-4 py-3 rounded-md bg-background-50 border text-sm md:text-base text-foreground-800 placeholder:text-foreground-500 focus:outline-none focus:ring-2 transition-colors ${
      hasError ? "border-accent-500 focus:ring-accent-300" : "border-background-200 focus:ring-primary-300"
    }`;

  return (
    <form
      id="mango-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      noValidate
      className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/80"
    >
      <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
        留下需求，我们尽快与你联系
      </h3>
      <p className="text-sm text-foreground-600 mt-2">
        填写下方信息，芒果传媒的团队将在 1-2 个工作日内与你取得联系。
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
            姓名 <span className="text-accent-600">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            value={values.name}
            onChange={handleChange("name")}
            placeholder="请输入您的姓名"
            className={inputClass(errors.name)}
          />
          {errors.name && <p className="text-xs text-accent-600 mt-1.5">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-800 mb-1.5">
            联系电话 <span className="text-accent-600">*</span>
          </label>
          <input
            id="contact-phone"
            name="phone"
            type="tel"
            value={values.phone}
            onChange={handleChange("phone")}
            placeholder="请输入您的手机号"
            className={inputClass(errors.phone)}
          />
          {errors.phone && <p className="text-xs text-accent-600 mt-1.5">{errors.phone}</p>}
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
          电子邮箱（选填）
        </label>
        <input
          id="contact-email"
          name="email"
          type="email"
          value={values.email}
          onChange={handleChange("email")}
          placeholder="name@example.com"
          className={inputClass(errors.email)}
        />
        {errors.email && <p className="text-xs text-accent-600 mt-1.5">{errors.email}</p>}
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between mb-1.5">
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800">
            留言内容 <span className="text-accent-600">*</span>
          </label>
          <span
            className={`text-xs ${values.message.length > MESSAGE_LIMIT ? "text-accent-600" : "text-foreground-500"}`}
          >
            {values.message.length} / {MESSAGE_LIMIT}
          </span>
        </div>
        <textarea
          id="contact-message"
          name="message"
          rows={5}
          maxLength={MESSAGE_LIMIT}
          value={values.message}
          onChange={handleChange("message")}
          placeholder="请简要描述你的合作需求或咨询内容…"
          className={inputClass(errors.message)}
        ></textarea>
        {errors.message && <p className="text-xs text-accent-600 mt-1.5">{errors.message}</p>}
      </div>

      <div className="field-optional-extra" aria-hidden="true">
        <label htmlFor="contact-alt">备用联系方式</label>
        <input
          id="contact-alt"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <button
        type="submit"
        disabled={status === "loading"}
        className="mt-7 w-full sm:w-auto px-8 py-3 rounded-md bg-primary-500 text-foreground-950 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "loading" ? "提交中…" : "提交咨询"}
      </button>

      {status === "success" && (
        <div className="mt-5 flex items-start gap-3 bg-secondary-50 border border-primary-200 rounded-md p-4">
          <i className="ri-checkbox-circle-fill text-primary-600 text-xl mt-0.5"></i>
          <div>
            <p className="font-medium text-foreground-900 text-sm">提交成功！</p>
            <p className="text-xs text-foreground-600 mt-1">
              感谢你的咨询，芒果传媒会尽快与你联系。（本表单为演示用途）
            </p>
          </div>
        </div>
      )}

      {status === "error" && (
        <div className="mt-5 flex items-start gap-3 bg-accent-50 border border-accent-300 rounded-md p-4">
          <i className="ri-error-warning-fill text-accent-600 text-xl mt-0.5"></i>
          <div>
            <p className="font-medium text-foreground-900 text-sm">提交失败</p>
            <p className="text-xs text-foreground-600 mt-1">{errorMsg}</p>
          </div>
        </div>
      )}
    </form>
  );
}